import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';
import { map, mergeMap, of } from 'rxjs';
import { BoardHolderService } from '../../core/board/board.holder.service';
import { CoreItem, CoreItemType } from '../../core/core-item/core-item';
import { CoreItemService } from '../../core/core-item/core-item.service';
import { GoalService } from '../../core/goal/goal.service';
import { InitAllBoardListService } from '../../core/init-data/init-all-board-list.service';
import { ResumeService } from '../../core/resume/resume.service';
import { UserClerkService } from '../../core/user/user-clerk.service';
import { WebSocketService } from '../../core/websocket/websocket.service';
import { WorkspaceService } from '../../core/workspace/workspace.service';
import { Id } from '../../shared/id/id';
import { WorkspaceHolderService } from './../../core/workspace/workspace.holder.service';

export const homeResolver = (route: ActivatedRouteSnapshot) => {
  const webSocketService = inject(WebSocketService);
  const userClerkService = inject(UserClerkService);
  const initAllBoardListService = inject(InitAllBoardListService);
  const workspaceHolderService = inject(WorkspaceHolderService);
  const coreItemService = inject(CoreItemService);
  const workspaceService = inject(WorkspaceService);
  const boardHolderService = inject(BoardHolderService);
  const goalService = inject(GoalService);
  const resumeService = inject(ResumeService);

  const router = inject(Router);

  webSocketService.connect();

  const id = route.paramMap.get('id');

  let workspaceId: string | undefined = undefined;
  if (Id.backend.chop(id)?.coreType === CoreItemType.workspace) {
    workspaceId = id === null ? undefined : id;
  }

  return userClerkService.getUser$().pipe(
    mergeMap((user) => {
      if (!user) {
        return of(false);
      }

      return initAllBoardListService.getAllBoardMetadataAndPersonalBoardAndChangeDefaultWorkspace$(workspaceId).pipe(
        map((data) => {
          if (data.isMaintainanceMode) {
            initAllBoardListService.isMaintainanceMode.set(true);
            initAllBoardListService.maintainanceText.set(data.maintainanceText!);

            router.navigate(['/maintainance']);
            return;
          }

          const defaultWorkspaceBackend = data.myWorkspaceList.find((c: any) => c.isDefault)!;

          const board = coreItemService.convertFromBackendToFrontend(data.myPersonalBoard);

          boardHolderService.myPersonalBoard.set(board);

          Sentry.setUser({
            username: boardHolderService.myPersonalBoard()?.userBoardConfig?.preferredName,
            email: user.primaryEmailAddress?.emailAddress,
          });

          const defaultWorkspace = workspaceService.convertFromBackendToFrontend(defaultWorkspaceBackend);

          if (defaultWorkspace.id !== workspaceHolderService.currentWorkspace()?.id) {
            workspaceHolderService.currentWorkspace.set(defaultWorkspace!);
            workspaceHolderService.fileList.set(data.fileList);
          }

          data.itemBackendList.map((c: any) => coreItemService.updateCoreItemInCache(c)) as CoreItem[];

          return data;
        }),
        mergeMap(() => {
          if (initAllBoardListService.isMaintainanceMode()) {
            return of([]);
          }
          return userClerkService.reloadUser$();
        }),
        mergeMap(() => {
          if (initAllBoardListService.isMaintainanceMode()) {
            return of([]);
          }
          return goalService.goalList$();
        }),
        mergeMap(() => {
          if (initAllBoardListService.isMaintainanceMode()) {
            return of([]);
          }
          return resumeService.resumeList$();
        })
      );
    })
  );
};
